/**
 * Passenger-leg route ETA (pickup → dropoff).
 * CONF-20260719-trip-route-eta — does not mutate end_time occupancy.
 * Follows Settings travel_time_mode; directions uses trip date+pickup for traffic.
 */

import { supabase } from '../config/supabase';
import { parseGeoPoint } from '../utils/formatters';
import { estimateTravelKm } from '../scheduling/travelTime';
import { fetchSystemSettings } from '../utils/tripActions';
import {
  estimateStaticTravelMinutes,
  minutesToTimeString,
  parseSchedulingSettings,
  routeOdKey,
  schedulingSettingsToTravelOptions,
  timeStringToMinutes,
} from '../scheduling/travelTime';
import { prefetchDirectionsTravelMinutes } from '../scheduling/prefetchDirectionsTravel';
import {
  buildTripDepartureIso,
  classifyDirectionsRouting,
  DEFAULT_TRIP_TIME_ZONE,
  resolveRouteDriveSourceAfterAttempt,
} from './tripDeparture';

export type RouteDriveSource = 'directions' | 'static' | 'static_fallback' | 'unavailable';

export interface TripRouteEtaFields {
  route_drive_minutes: number | null;
  route_drive_distance_meters: number | null;
  estimated_dropoff_time: string | null;
  route_drive_source: RouteDriveSource;
  route_drive_updated_at: string;
}

function nowIso(): string {
  return new Date().toISOString();
}

function unavailable(): TripRouteEtaFields {
  return {
    route_drive_minutes: null,
    route_drive_distance_meters: null,
    estimated_dropoff_time: null,
    route_drive_source: 'unavailable',
    route_drive_updated_at: nowIso(),
  };
}

/** Add minutes to HH:mm[:ss]; MVP caps at 23:59:59 same day. */
export function estimatedDropoffFromPickup(
  pickupTime: string,
  driveMinutes: number
): string {
  const base = timeStringToMinutes(pickupTime.slice(0, 8));
  const total = Math.max(0, base + Math.max(0, Math.ceil(driveMinutes)));
  const capped = Math.min(total, 23 * 60 + 59);
  return minutesToTimeString(capped);
}

/**
 * Compute ETA fields from geopoints + pickup time (+ optional schedule date).
 * Follows Settings travel_time_mode (directions → Google/cache; else static).
 * On Google failure in directions mode → static_fallback (UI: 静态·谷歌失败).
 */
export async function computeTripRouteEta(params: {
  pickupGeopoint: string | null | undefined;
  dropoffGeopoint: string | null | undefined;
  pickupTime: string | null | undefined;
  /** YYYY-MM-DD — used with pickupTime for traffic-aware Google departure. */
  date?: string | null;
  timeZone?: string;
}): Promise<TripRouteEtaFields> {
  const pickup = params.pickupGeopoint
    ? parseGeoPoint(params.pickupGeopoint)
    : null;
  const dropoff = params.dropoffGeopoint
    ? parseGeoPoint(params.dropoffGeopoint)
    : null;
  const pickupTime = params.pickupTime?.trim() || null;

  if (!pickup || !dropoff || !pickupTime) {
    return unavailable();
  }

  const settings = parseSchedulingSettings(await fetchSystemSettings());
  const travelOpts = schedulingSettingsToTravelOptions(settings);
  const key = routeOdKey(pickup, dropoff);
  const staticKm = estimateTravelKm(pickup, dropoff, travelOpts);
  const staticDistanceMeters = Math.round(staticKm * 1000);

  let minutes: number;
  let distanceMeters: number | null;
  let source: RouteDriveSource;

  if (settings.travel_time_mode === 'directions') {
    const date = params.date?.trim() || null;
    const departureIso = date
      ? buildTripDepartureIso(date, pickupTime, params.timeZone ?? DEFAULT_TRIP_TIME_ZONE)
      : null;
    const routing = classifyDirectionsRouting(departureIso);

    const pref = await prefetchDirectionsTravelMinutes([{ from: pickup, to: dropoff }], {
      routingPreference: routing.routingPreference,
      departureTimeIso: routing.departureTimeIso,
      bypassCache: true,
    });
    const keyed = pref.directionsMinutes[key];
    if (typeof keyed === 'number' && Number.isFinite(keyed) && keyed >= 0) {
      minutes = keyed === 0 ? 0 : Math.max(1, Math.ceil(keyed));
      const dist = pref.directionsDistances[key];
      distanceMeters =
        typeof dist === 'number' && Number.isFinite(dist) ? Math.round(dist) : staticDistanceMeters;
      source = resolveRouteDriveSourceAfterAttempt({ mode: 'directions', googleOk: true });
    } else {
      minutes = estimateStaticTravelMinutes(pickup, dropoff, travelOpts);
      distanceMeters = staticDistanceMeters;
      source = resolveRouteDriveSourceAfterAttempt({ mode: 'directions', googleOk: false });
    }
  } else {
    minutes = estimateStaticTravelMinutes(pickup, dropoff, travelOpts);
    distanceMeters = staticDistanceMeters;
    source = resolveRouteDriveSourceAfterAttempt({ mode: 'static', googleOk: false });
  }

  return {
    route_drive_minutes: minutes,
    route_drive_distance_meters: distanceMeters,
    estimated_dropoff_time: estimatedDropoffFromPickup(pickupTime, minutes),
    route_drive_source: source,
    route_drive_updated_at: nowIso(),
  };
}

/** Patch a single trip's ETA columns (does not touch end_time). */
export async function applyTripRouteEta(
  tripId: string,
  fields: TripRouteEtaFields
): Promise<void> {
  const { error } = await supabase
    .from('trips')
    .update({
      route_drive_minutes: fields.route_drive_minutes,
      route_drive_distance_meters: fields.route_drive_distance_meters,
      estimated_dropoff_time: fields.estimated_dropoff_time,
      route_drive_source: fields.route_drive_source,
      route_drive_updated_at: fields.route_drive_updated_at,
    })
    .eq('id', tripId);
  if (error) throw new Error(error.message);
}

export async function refreshTripRouteEtaById(tripId: string): Promise<TripRouteEtaFields> {
  const { data, error } = await supabase
    .from('trips')
    .select('id, date, pickup_time, pickup_geopoint, dropoff_geopoint')
    .eq('id', tripId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error('Trip not found');

  const fields = await computeTripRouteEta({
    pickupGeopoint: data.pickup_geopoint as string | null,
    dropoffGeopoint: data.dropoff_geopoint as string | null,
    pickupTime: data.pickup_time as string | null,
    date: data.date as string | null,
  });
  await applyTripRouteEta(tripId, fields);
  return fields;
}

/** Refresh ETA for all non-cancelled trips on a date (e.g. after publish). */
export async function refreshTripsRouteEtaForDate(scheduleDate: string): Promise<number> {
  const { data, error } = await supabase
    .from('trips')
    .select('id, date, pickup_time, pickup_geopoint, dropoff_geopoint')
    .eq('date', scheduleDate)
    .neq('status', 'cancelled');
  if (error) throw new Error(error.message);

  let updated = 0;
  for (const row of data ?? []) {
    const fields = await computeTripRouteEta({
      pickupGeopoint: row.pickup_geopoint as string | null,
      dropoffGeopoint: row.dropoff_geopoint as string | null,
      pickupTime: row.pickup_time as string | null,
      date: (row.date as string | null) ?? scheduleDate,
    });
    await applyTripRouteEta(row.id as string, fields);
    updated += 1;
  }
  return updated;
}
